import { FC } from 'react';
import { Tooltip } from 'react-tooltip';

import { Envelope, EnvelopeWithStatus } from 'types/Envelope';

import { hasInternalView } from 'utils/showDetails';

import Button from 'modules/_shared/components/Buttons';
import MenuDots from 'modules/_shared/components/MenuDots';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import {
  formatDateTimeCell,
  formatSignersCell,
  formatStatusCell,
} from 'modules/envelopes/utils/formatEnvelopeCells';

import { useTranslate } from 'hooks/useTranslate';
import { useEnvelopesListRecentsSectionRow } from '../hooks';

type EnvelopesListRecentsSectionRowProps = {
  envelope: EnvelopeWithStatus;
  envelopes: Envelope[];
  index: number;
  showAllColumns: boolean;
  onRefresh?: () => Promise<void>;
};

const EnvelopesListRecentsSectionRow: FC<
  EnvelopesListRecentsSectionRowProps
> = ({ index, envelope, envelopes, showAllColumns, onRefresh }) => {
  const { t } = useTranslate();

  const { data, isLoading, isError, actions } =
    useEnvelopesListRecentsSectionRow({
      envelope,
      envelopes,
      onRefresh,
    });

  const {
    user,
    actualSociety,
    isAdmin,
    currentMenuOptions,
    isResendButtonDisabled,
    isCancelButtonDisabled,
    isRefreshButtonDisabled,
    mixpanel,
  } = data;

  const {
    handleClickSubject,
    handleClickResend,
    handleClickCancel,
    handleClickRefresh,
  } = actions;

  return (
    <>
      <tr
        key={envelope.envelopeStatus?.envelopeInfo.envelopeId}
        className="nk-tb-item cursor-pointer"
      >
        <td className="nk-tb-col cell-index d-none d-md-table-cell">
          <span className="cell-index-text">{index + 1}</span>
        </td>

        {hasInternalView() && (
          <td className="nk-tb-col cell-envelope-id">
            <span className="cell-envelope-id-text">
              {envelope.envelopeStatus?.envelopeInfo.envelopeId}
            </span>
          </td>
        )}

        <td className="nk-tb-col cell-email-subject">
          <span
            className="cell-email-subject-text cursor-pointer"
            onClick={() => handleClickSubject(envelope)}
            style={{ cursor: 'pointer' }}
          >
            {envelope.document?.name ||
              envelope.envelopeStatus?.envelopeInfo.emailSubject}
          </span>
        </td>

        <td className="nk-tb-col cell-sent-date d-none d-md-table-cell">
          <span className="cell-sent-date-text">
            {formatDateTimeCell(
              envelope.envelopeStatus?.envelopeInfo.sentDateTime
            )}
          </span>
        </td>

        {showAllColumns && (
          <>
            <td className="nk-tb-col cell-expire-date">
              <span className="cell-expire-date-text">
                {formatDateTimeCell(
                  envelope.envelopeStatus?.envelopeInfo.expireDateTime
                )}
              </span>
            </td>

            <td className="nk-tb-col cell-last-change-date">
              <span className="cell-last-change-date-text">
                {formatDateTimeCell(
                  envelope.envelopeStatus?.envelopeInfo.statusChangedDateTime
                )}
              </span>
            </td>
          </>
        )}

        <td className="nk-tb-col cell-status">
          <span className="cell-status-text">{formatStatusCell(envelope)}</span>
          {!isRefreshButtonDisabled && (
            <>
              <Button
                data-tooltip-id={`refresh-status-button-tooltip-${index}`}
                data-tooltip-content={t('RefreshInvitationLetterStatus')}
                className="refresh-status-button rounded-circle ml-2"
                icon="ni ni-update"
                variant="primary"
                onClick={handleClickRefresh}
              />
              <Tooltip
                id={`refresh-status-button-tooltip-${index}`}
                place="top"
              />
            </>
          )}
        </td>

        {showAllColumns && (
          <>
            <td className="nk-tb-col cell-all-signed">
              <span className="cell-all-signed-text">
                {envelope.envelopeStatus?.signersStatus.allSigned ? 'Sí' : 'No'}
              </span>
            </td>

            <td className="nk-tb-col cell-pending-signers">
              <span className="cell-pending-signers-text">
                {formatSignersCell(
                  envelope.envelopeStatus?.signersStatus.pendingSigners || []
                )}
              </span>
            </td>

            <td className="nk-tb-col cell-completed-signers">
              <span className="cell-completed-signers-text">
                {formatSignersCell(
                  envelope.envelopeStatus?.signersStatus.completedSigners || []
                )}
              </span>
            </td>

            <td className="nk-tb-col cell-declined-signers">
              <span className="cell-declined-signers-text">
                {formatSignersCell(
                  envelope.envelopeStatus?.signersStatus.declinedSigners || []
                )}
              </span>
            </td>
          </>
        )}

        {hasInternalView() && (
          <td className="nk-tb-col cell-should-resend">
            <span className="cell-should-resend-text">
              {envelope.envelopeStatus?.signersStatus.shouldResend
                ? 'Yes'
                : 'No'}
            </span>
          </td>
        )}

        <td className="nk-tb-col cell-summary d-none d-md-table-cell">
          <span className="cell-summary-text">
            {envelope.envelopeStatus?.signersStatus.summary.completed}/
            {envelope.envelopeStatus?.signersStatus.summary.total}
          </span>
        </td>

        <td className="nk-tb-col cell-actions d-none d-lg-table-cell">
          <div className="position-absolute top-0 right-0 mt-2 mr-4 z-1000">
            <MenuDots
              menuOptions={currentMenuOptions}
              // @ts-ignore
              id={envelope._id}
              params={{
                user,
                envelope,
                actualSociety,
                mixpanel,
                isReadOnly: !isAdmin,
                isCancelButtonDisabled,
                isResendButtonDisabled,
                onClickResend: () => handleClickResend(),
                onClickCancel: () => handleClickCancel(),
              }}
              direction="right"
            />
          </div>
        </td>
      </tr>

      {isLoading && <CustomLoading />}
      {isError && (
        <tr>
          <td
            colSpan={showAllColumns ? 12 : 8}
            className="text-center text-danger"
          >
            An error occurred while processing this envelope
          </td>
        </tr>
      )}
    </>
  );
};

export default EnvelopesListRecentsSectionRow;
